import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_MAX_RETRIES = 5;
const DEFAULT_BASE_DELAY_MS = 1000;
const DEFAULT_MAX_DELAY_MS = 30000;
const COUNTDOWN_INTERVAL_MS = 1000;

interface UseRetryWithBackoffOptions {
  /** Maximum retry attempts before further retries are blocked. Default 5. */
  maxRetries?: number;
  /** Base delay in ms for the exponential curve. Default 1000. */
  baseDelay?: number;
  /** Delay cap in ms. Default 30000. */
  maxDelay?: number;
}

interface UseRetryWithBackoffResult {
  /** Runs the action after the current backoff delay elapses */
  retry: (action: () => void | Promise<void>) => void;
  /** Clears pending retries and resets the attempt count */
  reset: () => void;
  /** True while a retry is delayed or executing */
  isRetrying: boolean;
  /** Seconds remaining before the delayed retry fires (0 when immediate) */
  countdown: number;
  attemptCount: number;
  /** True when maxRetries has been reached and no more retries will fire */
  isExhausted: boolean;
}

/**
 * Retry with exponential backoff to prevent hammering an unhealthy server.
 * Delay progression: 0s → 1s → 2s → 4s → 8s → reset (defaults).
 *
 * @example
 * const { retry, isRetrying, countdown } = useRetryWithBackoff();
 *
 * <ErrorDisplay
 *   error={error}
 *   displayType="tab"
 *   primaryButton={{
 *     title: isRetrying ? `Retrying in ${countdown}s...` : 'Try again',
 *     onPress: () => retry(refetch),
 *   }}
 * />
 */
export const useRetryWithBackoff = (
  options: UseRetryWithBackoffOptions = {},
): UseRetryWithBackoffResult => {
  const {
    maxRetries = DEFAULT_MAX_RETRIES,
    baseDelay = DEFAULT_BASE_DELAY_MS,
    maxDelay = DEFAULT_MAX_DELAY_MS,
  } = options;

  const [attemptCount, setAttemptCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const attemptRef = useRef(0);
  const timersRef = useRef<{
    countdownInterval?: ReturnType<typeof setInterval>;
    retryTimeout?: ReturnType<typeof setTimeout>;
  }>({});

  const clearTimers = useCallback(() => {
    if (timersRef.current.countdownInterval) {
      clearInterval(timersRef.current.countdownInterval);
    }
    if (timersRef.current.retryTimeout) {
      clearTimeout(timersRef.current.retryTimeout);
    }
    timersRef.current = {};
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const isExhausted = attemptCount >= maxRetries;

  const retry = useCallback(
    (action: () => void | Promise<void>) => {
      if (
        timersRef.current.retryTimeout ||
        timersRef.current.countdownInterval
      ) {
        return;
      }

      if (attemptRef.current >= maxRetries) {
        return;
      }

      const currentAttempt = attemptRef.current;
      const delayMs =
        currentAttempt === 0
          ? 0
          : Math.min(baseDelay * Math.pow(2, currentAttempt - 1), maxDelay);

      attemptRef.current += 1;
      setAttemptCount(attemptRef.current);
      setIsRetrying(true);

      const runAction = () => {
        clearTimers();
        setCountdown(0);
        Promise.resolve(action())
          .catch(() => {})
          .finally(() => setIsRetrying(false));
      };

      if (delayMs === 0) {
        runAction();
        return;
      }

      setCountdown(Math.ceil(delayMs / COUNTDOWN_INTERVAL_MS));
      timersRef.current.countdownInterval = setInterval(() => {
        setCountdown((previousCountdown) =>
          previousCountdown > 0 ? previousCountdown - 1 : 0,
        );
      }, COUNTDOWN_INTERVAL_MS);
      timersRef.current.retryTimeout = setTimeout(runAction, delayMs);
    },
    [baseDelay, maxDelay, maxRetries, clearTimers],
  );

  const reset = useCallback(() => {
    clearTimers();
    attemptRef.current = 0;
    setAttemptCount(0);
    setIsRetrying(false);
    setCountdown(0);
  }, [clearTimers]);

  return { retry, reset, isRetrying, countdown, attemptCount, isExhausted };
};
